import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { cn } from '@heidi/utils';
import * as m from 'motion/react-m';
import { useEffect, useState, type FC } from 'react';

interface BreathingLogoMotifAnimationProps {
  className?: string;
  size?: number;
  variant?: 'default' | 'yellow';
}

const BreathingLogoMotifAnimation: FC<BreathingLogoMotifAnimationProps> = ({
  className = '',
  size = 100,
  variant = 'default',
}) => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setReduced(Boolean(mq.matches));
    // initialize
    handleChange();

    // Prefer modern API
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handleChange);
      return () => {
        mq.removeEventListener('change', handleChange);
      };
    }

    // Legacy API typings for older browsers
    type LegacyMQ = MediaQueryList & {
      addListener?: (
        listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void
      ) => void;
      removeListener?: (
        listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void
      ) => void;
    };
    const legacy = mq as LegacyMQ;
    const legacyHandler = () => handleChange();
    legacy.addListener?.(legacyHandler);
    return () => {
      legacy.removeListener?.(legacyHandler);
    };
  }, []);

  const mark = (
    <HeidiLogoMark
      artwork={size > 20 ? 'standard' : 'compact'}
      // theme-agnostic: brand sunlight accent on the Heidi mark, the same yellow in both themes
      className={variant === 'yellow' ? 'text-sunlight-400' : 'text-current'}
      style={{ width: size, height: size }}
    />
  );

  if (reduced) {
    return <span className={cn('inline-block', className)}>{mark}</span>;
  }

  /**
   * Easter egg: the animation follows the 4-7-8 breathing technique
   * (Dr. Andrew Weil, rooted in pranayama) — a well-known relaxation method:
   *
   *   4 s  inhale  — gentle scale up   (0.97 → 1.03)
   *   7 s  hold    — rest at peak       (1.03 → 1.03)
   *   8 s  exhale  — slow scale down    (1.03 → 0.97)
   *   ─────────────────────────────────
   *   19 s total cycle  (~3.2 breaths/min)
   *
   * The effect is intentionally subtle (6 % scale range, 10 % opacity range)
   * so it reads as a calm, living presence rather than an obvious pulsing
   * animation — noticeable if you linger, invisible if you don't.
   */
  return (
    <m.div
      className={className}
      style={{ display: 'inline-block', willChange: 'transform, opacity' }}
      animate={{
        scale: [0.97, 1.03, 1.03, 0.97],
        opacity: [0.9, 1, 1, 0.9],
      }}
      transition={{
        duration: 19,
        times: [0, 0.21, 0.58, 1], // 4/19 ≈ 0.21, 11/19 ≈ 0.58
        ease: ['easeInOut', 'linear', 'easeInOut'],
        repeat: Infinity,
      }}
    >
      {mark}
    </m.div>
  );
};

export default BreathingLogoMotifAnimation;
